/**
 * Customer Panel Utility Functions
 * 
 * Merkezi utility fonksiyonları - OOP prensiplerine uygun, tekrar eden kodları önler
 */

import { UrlBuilder } from '@/lib/url-builder';
import type { PageUrlMap } from '@/types/blog';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';
import { useCallback } from 'react';

/**
 * URL builder helper - Customer panel için
 * Tekrar eden kodları önlemek için merkezi utility
 * Güvenli: URL bulunamazsa '#' döndürür, hata fırlatmaz
 */
export function buildCustomerPanelUrl(
  templateKey: string,
  pageUrls: PageUrlMap | undefined,
  language: string,
  defaultLanguage: string
): string {
  if (!pageUrls) {
    return '#';
  }
  
  try {
    const builder = new UrlBuilder(pageUrls, language, defaultLanguage);
    const url = builder.buildCustomerPanelUrl(templateKey);
    // URL boş veya geçersizse '#' döndür
    return url || '#';
  } catch (error) {
    // Hata durumunda '#' döndür (hata fırlatma)
    return '#';
  }
}

/**
 * Translation helper - Customer panel için
 * Tekrar eden kodları önlemek için merkezi utility
 */
export function getCustomerPanelTranslation(
  key: string,
  fallback: string,
  translations: Record<string, string> | undefined,
  getVariable: (key: string, fallback: string) => string
): string {
  return translations?.[key] || getVariable(key, fallback);
}

/**
 * Customer panel için merkezi URL builder hook
 */
export function useCustomerPanelUrlBuilder(
  pageUrls: PageUrlMap | undefined,
  language: string,
  defaultLanguage: string
) {
  return useCallback((templateKey: string): string => {
    return buildCustomerPanelUrl(templateKey, pageUrls, language, defaultLanguage);
  }, [pageUrls, language, defaultLanguage]);
}

/**
 * Customer panel için merkezi translation hook.
 * Öncelik: sayfa translations → LanguageVariablesContext (DB) → boş.
 * Hardcode fallback kullanılmaz; eksik anahtarlar seeder ile tamamlanmalıdır.
 */
export function useCustomerPanelTranslations(
  translations: Record<string, string> | undefined
) {
  const { getVariable } = useLanguageVariables();
  return useCallback((key: string, fallback = ''): string => {
    const fromPanel = translations?.[key]?.trim();
    if (fromPanel) {
      return fromPanel;
    }
    const fromDb = getVariable(key, '').trim();
    if (fromDb) {
      return fromDb;
    }
    return fallback;
  }, [translations, getVariable]);
}

